import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, BookOpen, HelpCircle, Layers, BookText, Phone,
  ChevronDown, ChevronRight, Rocket, Target, SlidersHorizontal,
  LineChart, Handshake, FileDown, Share2, Shield, Zap, Building2,
  Mail, MessageCircle, ThumbsUp, ThumbsDown,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

// ── Data ──

interface Article {
  id: string;
  title: string;
  content: string;
  tags: string[];
}

interface Section {
  id: string;
  title: string;
  icon: React.ElementType;
  description: string;
  articles: Article[];
}

const helpSections: Section[] = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: Rocket,
    description: "Learn the basics of analyzing your first deal",
    articles: [
      {
        id: "gs-1",
        title: "What is Brickshare?",
        content: "Brickshare is an AI-powered deal analysis platform for commercial real estate. It helps brokers, investors, and acquisition teams evaluate multifamily deals with institutional-grade underwriting in minutes — not days. Simply enter your deal parameters and Brickshare scores the opportunity across four pillars: Returns, Risk, Realism, and Confidence.",
        tags: ["overview", "introduction"],
      },
      {
        id: "gs-2",
        title: "How do I start a new deal?",
        content: "Click 'Start New Deal' in the sidebar. You'll begin at the Strategy Gate where you define the asset type (e.g. Multifamily), exit strategy (Value-Add, Core-Plus, Refinance Hold), unit count, market type, and hold period. Once saved, you'll move to Quick Screen to enter financial details.",
        tags: ["new deal", "workflow"],
      },
      {
        id: "gs-3",
        title: "What is the deal workflow?",
        content: "The deal workflow follows a structured path: 1) Strategy Gate — define asset profile, 2) Quick Screen — enter acquisition, income, expense, and debt inputs, 3) Score Results — view your 4-pillar score and verdict, 4) Assumptions — review and adjust underwriting parameters, 5) Sensitivity — stress-test across 6 scenarios, 6) Negotiation Summary — generate a broker-ready AI summary, 7) Export — download PDF or share the deal package.",
        tags: ["workflow", "steps", "process"],
      },
      {
        id: "gs-4",
        title: "How is the Deal Score calculated?",
        content: "The Deal Score (0–100) is computed across four weighted pillars: Returns Potential (financial upside metrics like IRR, CoC, equity creation), Risk Assessment (DSCR coverage, vacancy exposure, leverage), Realism Check (input completeness, assumption plausibility, CapEx benchmarking), and Market Confidence (data quality, comp availability). Each pillar contributes to the overall score, and the verdict (GO / PROCEED WITH CAUTION / PASS ON DEAL) is determined by your configured thresholds in Settings.",
        tags: ["score", "calculation", "pillars"],
      },
    ],
  },
  {
    id: "features",
    title: "Feature Documentation",
    icon: Layers,
    description: "Deep dives into each platform capability",
    articles: [
      {
        id: "ft-1",
        title: "Quick Screen",
        content: "Quick Screen is the rapid underwriting module where you enter deal financials. It captures: Acquisition (purchase price, CapEx, closing costs), Income (in-place rent, market rent, vacancy, GPR), Expenses (operating expenses, expense ratio, management fees), and Debt (loan amount, LTV, interest rate, amortization). Brickshare auto-calculates derived metrics like NOI, DSCR, Cap Rate, and Proxy IRR from your inputs.",
        tags: ["quick screen", "inputs", "underwriting"],
      },
      {
        id: "ft-2",
        title: "Score Results & Deal Health",
        content: "The Score Results page is your decision control center. It displays: the Deal Health Meter (4-pillar radar chart), Missing Data Indicator (shows what inputs would improve confidence), Make-It-Work Console (slider-based renegotiation tool to find deal terms that hit your targets), Recommended Next Steps (auto-generated due diligence checklist), and Version History (track how your score changes across iterations).",
        tags: ["results", "score", "health meter", "make it work"],
      },
      {
        id: "ft-3",
        title: "Sensitivity Analysis",
        content: "The Sensitivity module stress-tests your deal across 6 pre-built scenarios: Exit Cap +50/+100 bps, CapEx +10%/+15%, Rent Premium -10%, and Interest Rate +100 bps. Each scenario recalculates the deal score and shows whether the verdict shifts. This helps you understand downside risk before committing capital.",
        tags: ["sensitivity", "stress test", "scenarios"],
      },
      {
        id: "ft-4",
        title: "Make-It-Work Console",
        content: "The Make-It-Work Console lets you adjust key deal levers (purchase price, CapEx, interest rate, LTV) via sliders to find the combination that meets your target thresholds. You can save configurations as Negotiation Offers, Conservative Cases, or Aggressive Cases for side-by-side comparison.",
        tags: ["make it work", "negotiation", "sliders"],
      },
      {
        id: "ft-5",
        title: "Deal Package (Shareable Summary)",
        content: "The Deal Package creates a professional investor-ready summary page you can share via link. It includes: deal header with verdict and score, key metrics, AI-generated opportunity summary, opportunity drivers, risk considerations, sensitivity snapshot, negotiation insights, and recommended next steps. You can customize which sections appear before generating the link.",
        tags: ["deal package", "share", "investor", "link"],
      },
      {
        id: "ft-6",
        title: "Negotiation Summary",
        content: "The Negotiation Summary uses AI to generate a grounded, broker-ready narrative based on your deal analysis. You can control the tone (Professional, Direct, Collaborative) and aggressiveness level. Generated summaries are saved for future reference and can be exported as PDFs or cloned into new scenarios.",
        tags: ["negotiation", "AI", "summary", "broker"],
      },
      {
        id: "ft-7",
        title: "Multi-Scenario Comparison",
        content: "Compare up to 4 saved scenarios side-by-side across key metrics: Deal Score, IRR, Cash-on-Cash, DSCR, Cap Rate, and Risk Level. You can promote any scenario to your primary analysis with one click. Delta indicators show how each scenario differs from your base case.",
        tags: ["scenarios", "comparison", "side by side"],
      },
      {
        id: "ft-8",
        title: "My Deals Portfolio",
        content: "My Deals is your management hub for all analyzed deals. Search, filter by verdict (GO / PROCEED WITH CAUTION / PASS ON DEAL), and sort by date, score, or units. Click any deal to resume the analysis workflow from where you left off. All deal data is persisted and syncs across sessions.",
        tags: ["my deals", "portfolio", "history"],
      },
    ],
  },
  {
    id: "faq",
    title: "Frequently Asked Questions",
    icon: HelpCircle,
    description: "Quick answers to common questions",
    articles: [
      {
        id: "faq-1",
        title: "Can I edit a deal after scoring it?",
        content: "Yes. Navigate to any scored deal and adjust inputs on the Quick Screen or Assumptions page. After making changes, click 'Recalculate' to update your score. The platform tracks version history so you can compare iterations.",
        tags: ["edit", "recalculate", "version"],
      },
      {
        id: "faq-2",
        title: "What does 'Needs Review' mean?",
        content: "When you modify any input after a score has been calculated, the deal enters a 'Needs Review' state. This indicates the displayed score may be stale. Click 'Recalculate' to refresh the analysis with your updated inputs.",
        tags: ["needs review", "stale", "recalculate"],
      },
      {
        id: "faq-3",
        title: "How do I share a deal with investors?",
        content: "From the Score Results page, click 'Deal Package' in the bottom action bar. Customize which sections to include, then click 'Generate Package.' A shareable link is copied to your clipboard. You can also use 'Copy Link' to share a direct URL to the results page, or 'Export PDF' for offline distribution.",
        tags: ["share", "deal package", "investors", "link"],
      },
      {
        id: "faq-4",
        title: "What are the verdict thresholds?",
        content: "By default, deals scoring ≥75 receive a 'GO' verdict, 60–74 receive 'PROCEED WITH CAUTION', and <60 receive 'PASS ON DEAL'. You can customize these thresholds in Settings → Scoring Thresholds to match your investment criteria.",
        tags: ["verdict", "thresholds", "go", "caution", "pass on deal"],
      },
      {
        id: "faq-5",
        title: "Can I change my target return metrics?",
        content: "Yes. Go to Settings and adjust your target IRR, Cash-on-Cash, and DSCR thresholds. These targets are used when calculating whether a deal meets your investment criteria. Changes apply to all future calculations.",
        tags: ["settings", "targets", "IRR", "CoC", "DSCR"],
      },
      {
        id: "faq-6",
        title: "Is my data secure?",
        content: "Yes. All deal data is encrypted in transit and at rest. Each user can only access their own deals — row-level security ensures complete data isolation. Shared deal links only expose the specific data you choose to include in the Deal Package.",
        tags: ["security", "privacy", "data"],
      },
      {
        id: "faq-7",
        title: "What asset types are supported?",
        content: "Brickshare currently specializes in multifamily (apartment) assets. The scoring engine is calibrated for multifamily underwriting metrics and benchmarks. Support for additional CRE asset classes is planned for future releases.",
        tags: ["asset types", "multifamily", "apartments"],
      },
    ],
  },
  {
    id: "glossary",
    title: "Glossary of Terms",
    icon: BookText,
    description: "CRE metrics and terminology explained",
    articles: [
      {
        id: "gl-1",
        title: "IRR (Internal Rate of Return)",
        content: "The annualized rate of return that makes the net present value of all cash flows equal to zero. In real estate, IRR accounts for both ongoing cash flow and the eventual sale/refinance. Brickshare estimates a proxy IRR based on cash-on-cash returns, equity creation, and hold period. A typical value-add multifamily target is 15–20% IRR.",
        tags: ["IRR", "returns", "metric"],
      },
      {
        id: "gl-2",
        title: "DSCR (Debt Service Coverage Ratio)",
        content: "Net Operating Income divided by annual debt service (principal + interest payments). DSCR measures how comfortably property income covers loan payments. A DSCR of 1.25x means the property generates 25% more income than needed to service debt. Most lenders require ≥1.20x; Brickshare defaults to a 1.25x target.",
        tags: ["DSCR", "debt", "coverage", "metric"],
      },
      {
        id: "gl-3",
        title: "Cap Rate (Capitalization Rate)",
        content: "Net Operating Income divided by property value (purchase price). Cap Rate represents the yield an investor would receive if they purchased the property all-cash. Going-in cap rate uses the purchase price; exit cap rate is the assumed rate at sale. A lower cap rate generally indicates lower risk / higher price.",
        tags: ["cap rate", "yield", "metric"],
      },
      {
        id: "gl-4",
        title: "NOI (Net Operating Income)",
        content: "Gross rental income minus vacancy loss minus operating expenses (excluding debt service). NOI is the fundamental measure of property-level profitability. Brickshare calculates both in-place NOI (current operations) and stabilized NOI (after renovations and lease-up to market rents).",
        tags: ["NOI", "income", "operating", "metric"],
      },
      {
        id: "gl-5",
        title: "Cash-on-Cash Return",
        content: "Annual pre-tax cash flow divided by total equity invested. This measures the cash yield on your actual out-of-pocket investment. Unlike IRR, it doesn't account for appreciation or the sale event. A typical value-add target is 8–12% stabilized CoC.",
        tags: ["cash on cash", "CoC", "returns", "metric"],
      },
      {
        id: "gl-6",
        title: "LTV (Loan-to-Value Ratio)",
        content: "The loan amount divided by the property's appraised value or purchase price. LTV represents leverage level — higher LTV means more debt relative to equity. Most conventional multifamily loans cap at 75–80% LTV. Higher leverage amplifies returns but also increases risk.",
        tags: ["LTV", "leverage", "loan", "metric"],
      },
      {
        id: "gl-7",
        title: "Breakeven Occupancy",
        content: "The minimum occupancy rate needed to cover all operating expenses and debt service. A breakeven occupancy of 48% means the property remains cash-flow positive even with 52% vacancy. Lower breakeven = more margin of safety.",
        tags: ["breakeven", "occupancy", "vacancy", "metric"],
      },
      {
        id: "gl-8",
        title: "Equity Created",
        content: "The difference between the stabilized property value (NOI ÷ exit cap rate) and the total cost basis (purchase price + CapEx + closing costs). Positive equity creation means the value-add strategy is expected to produce a profit above and beyond the investment. Expressed as both a dollar amount and a percentage.",
        tags: ["equity", "value creation", "metric"],
      },
      {
        id: "gl-9",
        title: "CapEx (Capital Expenditures)",
        content: "Funds spent on property improvements, renovations, or major repairs that add value beyond routine maintenance. In multifamily value-add, CapEx typically includes unit renovations (kitchen, bath, flooring), common area upgrades, and deferred maintenance. Brickshare benchmarks CapEx per unit against age-based bands.",
        tags: ["CapEx", "renovations", "capital", "metric"],
      },
    ],
  },
];

const contactInfo = {
  email: "support@brickshare.ai",
  phone: "+1 (888) 274-2574",
  hours: "Monday – Friday, 9 AM – 6 PM EST",
};

// ── Components ──

const ArticleCard = ({ article, isOpen, onToggle, feedback, onFeedback }: {
  article: Article; isOpen: boolean; onToggle: () => void;
  feedback: "yes" | "no" | null; onFeedback: (articleId: string, value: "yes" | "no") => void;
}) => (
  <motion.div
    id={`article-${article.id}`}
    layout
    className={`border rounded-lg overflow-hidden bg-card transition-colors ${isOpen ? "border-accent/40 ring-1 ring-accent/20" : "border-border hover:border-accent/30"}`}
  >
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-3.5 text-left gap-3"
    >
      <span className="text-sm font-medium text-foreground">{article.title}</span>
      {isOpen ? (
        <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
      ) : (
        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
      )}
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <div className="px-4 pb-4 border-t border-border/50 pt-3">
            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{article.content}</p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {article.tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="font-mono text-2xs px-2 py-0.5">
                  {tag}
                </Badge>
              ))}
            </div>
            {/* Feedback */}
            <div className="mt-4 pt-3 border-t border-border/30 flex items-center gap-3">
              <span className="text-xs text-muted-foreground">Was this helpful?</span>
              {feedback ? (
                <span className="text-xs text-accent font-medium">Thanks for your feedback!</span>
              ) : (
                <div className="flex gap-1.5">
                  <button
                    onClick={(e) => { e.stopPropagation(); onFeedback(article.id, "yes"); }}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-border hover:border-accent/40 hover:bg-accent/5 text-muted-foreground hover:text-accent transition-colors"
                  >
                    <ThumbsUp className="w-3 h-3" /> Yes
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onFeedback(article.id, "no"); }}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-border hover:border-destructive/40 hover:bg-destructive/5 text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <ThumbsDown className="w-3 h-3" /> No
                  </button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const HelpCenter = () => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [openArticles, setOpenArticles] = useState<Set<string>>(new Set());
  const [activeSection, setActiveSection] = useState<string | null>(null);

  // Auto-open article from ?article= query param (linked from HelpTooltip)
  useEffect(() => {
    const articleId = searchParams.get("article");
    if (articleId) {
      setOpenArticles(new Set([articleId]));
      // Scroll to article after render
      setTimeout(() => {
        const el = document.getElementById(`article-${articleId}`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, [searchParams]);

  const toggleArticle = (id: string) => {
    setOpenArticles((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const [articleFeedback, setArticleFeedback] = useState<Record<string, "yes" | "no">>({});

  // Load existing feedback from DB
  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await (supabase as any)
        .from("article_feedback")
        .select("article_id, helpful")
        .eq("user_id", user.id);
      if (data) {
        const map: Record<string, "yes" | "no"> = {};
        for (const row of data) {
          map[row.article_id] = row.helpful ? "yes" : "no";
        }
        setArticleFeedback(map);
      }
    })();
  }, []);

  const handleFeedback = async (articleId: string, value: "yes" | "no") => {
    setArticleFeedback(prev => ({ ...prev, [articleId]: value }));
    toast({
      title: value === "yes" ? "Glad it helped!" : "We'll improve this",
      description: value === "yes" ? "Thanks for the positive feedback." : "Thanks — we'll work on making this clearer.",
    });
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await (supabase as any)
      .from("article_feedback")
      .upsert({
        user_id: user.id,
        article_id: articleId,
        helpful: value === "yes",
      }, { onConflict: "user_id,article_id" });
  };

  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return helpSections;
    const q = searchQuery.toLowerCase();
    return helpSections
      .map((section) => ({
        ...section,
        articles: section.articles.filter(
          (a) =>
            a.title.toLowerCase().includes(q) ||
            a.content.toLowerCase().includes(q) ||
            a.tags.some((t) => t.toLowerCase().includes(q))
        ),
      }))
      .filter((s) => s.articles.length > 0);
  }, [searchQuery]);

  const totalResults = filteredSections.reduce((sum, s) => sum + s.articles.length, 0);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-2"
        >
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-accent" />
            <span className="font-mono text-2xs uppercase tracking-widest text-muted-foreground">Support</span>
          </div>
          <h1 className="font-display text-3xl font-bold text-foreground tracking-tight">Help Center</h1>
          <p className="text-sm text-muted-foreground max-w-lg">
            Find answers, learn features, and get the most out of Brickshare.
          </p>
        </motion.div>

        {/* ── Search ── */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="relative"
        >
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search articles, features, or terms…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-11 font-mono text-sm bg-card border-border"
          />
          {searchQuery && (
            <span className="absolute right-3.5 top-1/2 -translate-y-1/2 font-mono text-2xs text-muted-foreground">
              {totalResults} result{totalResults !== 1 ? "s" : ""}
            </span>
          )}
        </motion.div>

        {/* ── Category Chips ── */}
        {!searchQuery && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="flex flex-wrap gap-2"
          >
            {helpSections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(isActive ? null : section.id)}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-lg border text-sm transition-all ${
                    isActive
                      ? "border-accent bg-accent/10 text-accent font-medium"
                      : "border-border bg-card text-foreground hover:border-accent/30"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {section.title}
                  <span className="font-mono text-2xs text-muted-foreground ml-1">{section.articles.length}</span>
                </button>
              );
            })}
          </motion.div>
        )}

        {/* ── Sections ── */}
        <div className="space-y-8">
          {filteredSections
            .filter((s) => !activeSection || s.id === activeSection)
            .map((section, sIdx) => {
              const Icon = section.icon;
              return (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * sIdx }}
                  className="space-y-3"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-accent" />
                    </div>
                    <div>
                      <h2 className="font-display text-lg font-semibold text-foreground">{section.title}</h2>
                      <p className="font-mono text-2xs text-muted-foreground">{section.description}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {section.articles.map((article) => (
                      <ArticleCard
                        key={article.id}
                        article={article}
                        isOpen={openArticles.has(article.id) || !!searchQuery}
                        onToggle={() => toggleArticle(article.id)}
                        feedback={articleFeedback[article.id] || null}
                        onFeedback={handleFeedback}
                      />
                    ))}
                  </div>
                </motion.div>
              );
            })}
        </div>

        {/* ── Contact / Support ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="rounded-2xl border border-border bg-card p-8 space-y-6"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
              <Phone className="w-4 h-4 text-accent" />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold text-foreground">Contact Support</h2>
              <p className="font-mono text-2xs text-muted-foreground">Need more help? Our team is here for you.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl border border-border bg-muted/20 space-y-2">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-accent" />
                <span className="micro-label text-muted-foreground">Email</span>
              </div>
              <a href={`mailto:${contactInfo.email}`} className="text-sm font-medium text-foreground hover:text-accent transition-colors block">
                {contactInfo.email}
              </a>
            </div>

            <div className="p-4 rounded-xl border border-border bg-muted/20 space-y-2">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-accent" />
                <span className="micro-label text-muted-foreground">Phone</span>
              </div>
              <a href={`tel:${contactInfo.phone.replace(/\D/g, "")}`} className="text-sm font-medium text-foreground hover:text-accent transition-colors block">
                {contactInfo.phone}
              </a>
            </div>

            <div className="p-4 rounded-xl border border-border bg-muted/20 space-y-2">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-accent" />
                <span className="micro-label text-muted-foreground">Hours</span>
              </div>
              <p className="text-sm font-medium text-foreground">{contactInfo.hours}</p>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default HelpCenter;
